class CustomScroller extends HTMLElement {
  constructor() {
    super();

    this.breakpoint = 768;
    this.mode = null;

    this.handleResize = this.handleResize.bind(this);
    this.handleTabClick = this.handleTabClick.bind(this);

    this.emblaNode = this.querySelector('.embla');
    this.viewportNode = this.emblaNode?.querySelector('.embla__viewport');
    this.emblaContainer = this.querySelector('.embla__container');
    this.slides = Array.from(this.querySelectorAll('.embla__slide'));

    this.prevBtn = this.querySelector('.embla__button--prev');
    this.nextBtn = this.querySelector('.embla__button--next');

    this.tabHeaders = this.closest('custom-tabs')
      ?.querySelectorAll('.custom-tab__header');

    this._wheelHandler = null; // store wheel handler for cleanup
  }

  connectedCallback() {
    this.init();

    window.addEventListener('resize', this.handleResize);

    // Listen to tab clicks
    this.tabHeaders?.forEach(tab =>
      tab.addEventListener('click', this.handleTabClick)
    );
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    this.tabHeaders?.forEach(tab =>
      tab.removeEventListener('click', this.handleTabClick)
    );
    this.destroyEmbla();
  }

  // -----------------------
  // Tab handling
  // -----------------------
  handleTabClick() {
    // wait until tab content is visible
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.refresh();
      });
    });
  }

  // -----------------------
  // Init / Refresh
  // -----------------------
  init() {
    const newMode = window.innerWidth <= this.breakpoint ? 'mobile' : 'desktop';
    this.mode = newMode;
    this.refresh();
  }

  refresh() {
    // Skip if hidden
    if (this.offsetParent === null) return;

    this.destroyEmbla();
    this.cacheSizes();
    this.setupLayout();
    this.createEmbla();
  }

  handleResize() {
    const newMode =
      window.innerWidth <= this.breakpoint ? 'mobile' : 'desktop';

    if (this.mode !== newMode) {
      this.mode = newMode;
      this.refresh();
    }
  }

  // -----------------------
  // Sizes
  // -----------------------
  cacheSizes() {
    const items = this.querySelectorAll('.custom-scroll__item');
    let maxHeight = 0;

    items.forEach(item => {
      const height = item.clientHeight;
      if (height > maxHeight) maxHeight = height;
    });

    this.slideSize = maxHeight;
  }

  setupLayout() {
    if (!this.viewportNode || !this.slideSize) return;

    if (this.mode === 'desktop') {
      this.viewportNode.style.minHeight = `${this.slideSize * 2}px`;
      this.emblaContainer.style.height = `${this.slideSize}px`;

      this.slides.forEach(slide => {
        slide.style.flex = `0 0 ${this.slideSize}px`;
      });
    } else {
      this.viewportNode.style.minHeight = '';
      this.emblaContainer.style.height = 'auto';

      this.slides.forEach(slide => {
        slide.style.flex = '0 0 80%';
      });
    }
  }

  // -----------------------
  // Embla
  // -----------------------
  createEmbla() {
    if (!this.viewportNode || !this.slideSize) return;

    this.emblaApi = EmblaCarousel(this.viewportNode, {
      axis: this.mode === 'mobile' ? 'x' : 'y',
      dragFree: false
    });

    this.emblaApi.on('select', this.updateActive.bind(this));
    this.updateActive();

    // Prevent text selection while dragging
    this.emblaApi.on('pointerDown', () => {
      document.body.style.userSelect = 'none';
    });

    this.emblaApi.on('pointerUp', () => {
      document.body.style.userSelect = '';
    });

    if (this.mode === 'desktop') {
      this.initWheelScroll();
    } else {
      this.initButtons();
    }
  }

  destroyEmbla() {
    if (this.emblaApi) {
      this.emblaApi.destroy();
      this.emblaApi = null;
    }

    // Remove mobile buttons listeners
    if (this.prevBtn) this.prevBtn.onclick = null;
    if (this.nextBtn) this.nextBtn.onclick = null;

    // Remove desktop wheel listener
    if (this.viewportNode && this._wheelHandler) {
      this.viewportNode.removeEventListener('wheel', this._wheelHandler);
      this._wheelHandler = null;
    }

    // Restore user-select in case drag ended mid-destroy
    document.body.style.userSelect = '';
  }

  // -----------------------
  // Active state
  // -----------------------
  updateActive() {
    const index = this.emblaApi.selectedScrollSnap();

    this.slides.forEach((slide, i) => {
      slide
        .querySelector('.custom-scroll__item')
        ?.classList.toggle('active', i === index);
    });
  }

  // -----------------------
  // Mobile buttons
  // -----------------------
  initButtons() {
    if (!this.prevBtn || !this.nextBtn) return;

    this.prevBtn.onclick = () => this.emblaApi.scrollPrev();
    this.nextBtn.onclick = () => this.emblaApi.scrollNext();
  }

  // -----------------------
  // Desktop wheel
  // -----------------------
  initWheelScroll() {
    let locked = false;

    this._wheelHandler = (e) => {
      e.preventDefault();
      if (locked) return;

      locked = true;
      e.deltaY > 0
        ? this.emblaApi.scrollNext()
        : this.emblaApi.scrollPrev();

      setTimeout(() => (locked = false), 350);
    };

    this.viewportNode.addEventListener('wheel', this._wheelHandler, { passive: false });
  }
}

if (!customElements.get('custom-scroller')) {
  customElements.define('custom-scroller', CustomScroller);
}
